import { useState } from 'react';
import { useSubmitContactForm } from '../hooks/useWebForm';
import { assetUrl } from '../utils/assetUrl';

// Sentinel for the role `<select>`. Renders as a disabled "Choose one…"
// placeholder option that's selected by default; HTML5 `required` on the
// select treats this empty value as invalid, so the form can't submit
// until the user picks a real role.
const ROLE_UNSELECTED = '';

/**
 * ContactPage — ported from hp-www-updraft/contact.html.
 *
 * Static marketing content with a contact form. Internal nav uses React
 * Router Link; assets resolve from public/assets/* (relative refs rewritten
 * to /assets/...). Wrapped by SiteLayout at the route level (App.tsx), so
 * this file omits the nav and footer.
 *
 * Submit posts to `POST /www/contact` via `useSubmitContactForm`. The
 * BE stamps `source = "contact"` server-side; we just send `formData`.
 * On success the form clears and a confirmation message replaces the
 * Send button until the user types again.
 */
export const ContactPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(ROLE_UNSELECTED);
  const [message, setMessage] = useState('');

  const submit = useSubmitContactForm();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit.mutate(
      { name, email, role, message },
      {
        onSuccess: () => {
          setName('');
          setEmail('');
          setRole(ROLE_UNSELECTED);
          setMessage('');
        },
      },
    );
  };

  // When the user starts editing again after a successful submit, drop
  // the "Thanks…" state so the form returns to its idle look. Same idea
  // if the previous attempt errored — they may be retrying.
  const onFieldFocus = () => {
    if (submit.isSuccess || submit.isError) submit.reset();
  };

  return (
    <>
      {/* HERO */}
      <section className="section section-brown hp-hero-pad-xl" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="ghost-logo">
          <img src={assetUrl('/logo-cream.svg')} alt="" width="618" height="100" />
        </div>
        <div className="container" style={{ position: 'relative', zIndex: '2', maxWidth: '900px' }}>
          <div className="eyebrow eyebrow-orange" style={{ marginBottom: '22px' }}>Contact</div>
          <h1 style={{ marginBottom: '24px' }}>Say hi.<br />We actually read these.</h1>
          <p className="lead" style={{ opacity: '0.8', maxWidth: '60ch' }}>
            Whether you're a chef, a curious eater, a regulator with questions, or a journalist on deadline — there's a person on the other end of every message.
          </p>
        </div>
      </section>

      {/* CONTACT OPTIONS */}
      <section className="section section-cream">
        <div className="container" style={{ maxWidth: '1100px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '48px' }}>
            <a href="#general" style={{ textDecoration: 'none', background: 'white', borderRadius: '22px', padding: '32px', border: '1px solid rgba(143,68,32,.08)', color: 'inherit', display: 'block', transition: 'transform .2s' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--hp-orange)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', marginBottom: '16px' }}>✉</div>
              <h4 style={{ marginBottom: '8px' }}>General</h4>
              <p style={{ margin: '0', fontSize: '14px', color: 'rgba(42,22,9,.7)' }}>Questions, feedback, just to say hi.</p>
              <p style={{ margin: '12px 0 0', fontSize: '14px', color: 'var(--hp-orange)', fontWeight: '600' }}>hello@homeplated.com</p>
            </a>
            <a href="#press" style={{ textDecoration: 'none', background: 'white', borderRadius: '22px', padding: '32px', border: '1px solid rgba(143,68,32,.08)', color: 'inherit', display: 'block' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--hp-green)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', marginBottom: '16px' }}>▣</div>
              <h4 style={{ marginBottom: '8px' }}>Press</h4>
              <p style={{ margin: '0', fontSize: '14px', color: 'rgba(42,22,9,.7)' }}>Media requests, interviews, assets.</p>
              <p style={{ margin: '12px 0 0', fontSize: '14px', color: 'var(--hp-green)', fontWeight: '600' }}>press@homeplated.com</p>
            </a>
            <a href="#regulators" style={{ textDecoration: 'none', background: 'white', borderRadius: '22px', padding: '32px', border: '1px solid rgba(143,68,32,.08)', color: 'inherit', display: 'block' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'var(--hp-blue)', color: 'var(--hp-brown-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', marginBottom: '16px' }}>⚖</div>
              <h4 style={{ marginBottom: '8px' }}>Regulators &amp; partners</h4>
              <p style={{ margin: '0', fontSize: '14px', color: 'rgba(42,22,9,.7)' }}>Health departments, cities, policy teams.</p>
              <p style={{ margin: '12px 0 0', fontSize: '14px', color: 'var(--hp-blue)', fontWeight: '600' }}>partners@homeplated.com</p>
            </a>
          </div>

          {/* MAIN FORM */}
          <div id="general" style={{ background: 'white', borderRadius: '28px', padding: '48px', border: '1px solid rgba(143,68,32,.08)', display: 'grid', gridTemplateColumns: '0.9fr 1.1fr', gap: '56px' }}>
            <div>
              <div className="eyebrow eyebrow-orange" style={{ marginBottom: '16px' }}>Or drop a line</div>
              <h3 style={{ marginBottom: '16px' }}>Send us a note</h3>
              <p style={{ color: 'rgba(42,22,9,.72)', fontSize: '15px', lineHeight: '1.55', margin: '0 0 16px' }}>
                If you're a chef interested in cooking for the network, an ambassador candidate, or a neighbor with a specific dish in mind — the form on the right goes straight to Peter.
              </p>
              <p style={{ color: 'rgba(42,22,9,.7)', fontSize: '14px', margin: '0' }}>Typical reply: within 48 hours, from a real person.</p>
            </div>
            {/*
              No `noValidate` here — we rely on the browser's built-in
              HTML5 validation so every `required` field (name, email,
              role, message) blocks submit and shows the native bubble
              if the user tries to send an empty form. Adding `noValidate`
              back would suppress that and silently send blank fields.
            */}
            <form id="contact-form" onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'grid', gap: '8px' }}>
                  <span className="form-field-label">Your name</span>
                  <input className="form-field" type="text" name="name" required value={name} onChange={(e) => setName(e.target.value)} onFocus={onFieldFocus} />
                </label>
                <label style={{ display: 'grid', gap: '8px' }}>
                  <span className="form-field-label">Email</span>
                  <input className="form-field" type="email" name="email" required value={email} onChange={(e) => setEmail(e.target.value)} onFocus={onFieldFocus} />
                </label>
              </div>
              <label style={{ display: 'grid', gap: '8px', marginBottom: '16px' }}>
                <span className="form-field-label">I'm reaching out as a…</span>
                <select className="form-field" name="role" required value={role} onChange={(e) => setRole(e.target.value)} onFocus={onFieldFocus}>
                  <option value="" disabled>Choose one…</option>
                  <option>Eater / neighbor</option>
                  <option>Home cook or chef</option>
                  <option>Ambassador candidate</option>
                  <option>Looking for work</option>
                  <option>Influencer</option>
                  <option>Investor</option>
                  <option>Just curious</option>
                  <option>Regulator or city partner</option>
                  <option>Journalist</option>
                  <option>Other</option>
                </select>
              </label>
              <label style={{ display: 'grid', gap: '8px', marginBottom: '24px' }}>
                <span className="form-field-label">What's on your mind?</span>
                <textarea className="form-field" name="message" rows={5} required style={{ resize: 'vertical' }} value={message} onChange={(e) => setMessage(e.target.value)} onFocus={onFieldFocus}></textarea>
              </label>
              {/*
                Right-align the submit button within the form column at a
                fixed 200px width, AND right-align its own content. The
                `.btn` class uses `display: inline-flex` (site.css), so
                `text-align: right` is a no-op on a flex container — we
                push the children with `justifyContent: 'flex-end'` on
                the button itself. The success copy ("Thanks — message
                received ✓") is longer than 200px would comfortably hold,
                so the button widens to fit when that state is active —
                better than truncating a confirm message that's only
                visible for a moment.
              */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  className="btn btn-orange"
                  style={{
                    width: submit.isSuccess ? 'auto' : '200px',
                    justifyContent: 'flex-end',
                  }}
                  disabled={submit.isPending}
                >
                  {submit.isPending ? 'Sending…' : submit.isSuccess ? 'Thanks — message received ✓' : 'Send note →'}
                </button>
              </div>
              {submit.isError && (
                <p role="alert" style={{ marginTop: '12px', color: 'var(--hp-orange)', fontSize: '14px' }}>
                  {submit.error.message}
                </p>
              )}
            </form>
            
          </div>
        </div>
      </section>
    </>
  );
};
